import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {Alert, Button, Divider, Space} from 'antd';
import {PlusOutlined} from '@ant-design/icons';
import {UnmountClosed} from 'react-collapse';
import {
  StyledBudgetRequestApprovalFlow,
  StyledBudgetRequestApprovalFlowDivider,
  StyledBudgetRequestApprovalFlowReview,
  StyledBudgetRequestApprovalFlowReviewActions,
  StyledBudgetRequestApprovalFlowReviewContainer,
  StyledBudgetRequestApprovalFlowDeleteModal,
} from './StyledBudgetRequestApprovalFlow';
import ApprovalModal from '../ApprovalModal';
import AddApprovalStepModal from '../AddApprovalStepModal';
import DeleteModal from '../../../DeleteModal';
import IconWrapper from '../../../IconWrapper';
import UserReview from '../../../UserReview';
import {EditLineIcon, TrashIcon} from '../../../../icons';
import {budgetHelpers, dateHelpers} from '../../../../utils/helpers';
import {budgetStatusConstants, DEFAULT_MODAL_PROPS} from '../../../../constants';
import {alertActions} from '../../../../state/actions/alert.actions';
import {budgetActions} from '../../../../state/actions/budget.actions';

const BudgetRequestApprovalFlow = ({
  ...rest
}) => {
  const [t] = useTranslation(['main', 'budgets']);
  const [approvalModalProps, setApprovalModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [approvalStepModalProps, setApprovalStepModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [deleteModalProps, setDeleteModalProps] = useState(DEFAULT_MODAL_PROPS);
  const [submitForReviewButtonProps, setSubmitForReviewButtonProps] = useState({loading: false, disabled: false});

  const dispatch = useDispatch();

  const {budget, employeeEmail, isAdmin} = useSelector(state => ({
    budget: state?.budget?.budgetRequest,
    employeeEmail: state?.user?.employee?.email,
    isAdmin: state?.user?.isAdmin,
  }));

  const {budgetId, budgetNumber} = useMemo(() => ({
    budgetId: budget?.id,
    budgetNumber: budgetHelpers.getBudgetNumber(budget)
  }), [budget?.id, budget?.bi_number, budget?.po_number]); // eslint-disable-line react-hooks/exhaustive-deps

  const isEnabledSendForApprovalButton = useMemo(() => {
    const isBudgetDefined = Boolean(budget?.budget_id);

    const isPOProcurementType = budgetHelpers.isPurchaseOrderBudget(budget);
    if (isPOProcurementType) {
      return Boolean(budget?.vendor_id) && isBudgetDefined;
    }
    return isBudgetDefined;
  }, [budget]);

  const {isDraftStatus, isInReviewStatus, isBudgetOwner} = useMemo(() => {
    return {
      isDraftStatus: budgetHelpers.isDraftBudget(budget),
      isInReviewStatus: budget?.status === budgetStatusConstants.IN_REVIEW,
      isBudgetOwner: budget?.owner?.email === employeeEmail,
    }
  }, [budget, employeeEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPOProcurementType = useMemo(() => {
    return budgetHelpers.isPurchaseOrderBudget(budget);
  }, [budget?.procurement_type]); // eslint-disable-line react-hooks/exhaustive-deps

  const approvals = useMemo(() => budget?.approval || [], [budget]);

  const getDeleteModalText = (key) => t(`budgets:modal.deleteApprovalStep.${key}`);

  const renderIcon = (icon) => <IconWrapper size={16}>{icon}</IconWrapper>;

  const renderReview = (review, idx) => {
    const isApproved = review?.is_approved;
    const isReadyReview = isApproved === Boolean(isApproved);
    const approvalDate = review?.approval_date;
    const date = dateHelpers.getDateFromISO(approvalDate, 'DD MMM YYYY, HH:mm');
    const isApprover = review?.approver?.email === employeeEmail;
    let canEdit = isInReviewStatus ? isApprover && !approvalDate : false;
    const canReassign = isInReviewStatus && isApprover;
    let status = isApproved === true
      ? 'approved' : isApproved === false ? 'rejected' : (isApprover && canEdit) ? 'pending' : 'default';
    const isLastReview = idx === approvals.length - 1;
    const isEditActionsVisible = isInReviewStatus && !isReadyReview && (isAdmin || isApprover);
    const canDelete = approvals.length > 1;
    const isRejectedEditable = status === 'rejected' && isApprover;
    let reviewer = {...review.approver};
    if (isApprover) reviewer.label = t('You');

    // if status is rejected, then allow to edit budget review
    if (isRejectedEditable) {
      canEdit = true;
    }

    if (idx > 0) {
      const previousReview = approvals[idx - 1];
      const previousIsApproved = Boolean(previousReview?.approval_date);
      if (!previousIsApproved) {
        // disable edit and reassign if previous review is not approved yet
        canEdit = false;
        status = 'default';
      }
    }

    return (
      <StyledBudgetRequestApprovalFlowReviewContainer last={`${isLastReview}`} direction='vertical' key={review.id}>
        <StyledBudgetRequestApprovalFlowReview>
          <UserReview
            commentType='collapsed'
            date={date}
            canEdit={canEdit}
            canReassign={canReassign}
            onApprove={() => handleOpenApproveModal(review, 'approve')}
            onEdit={() => isRejectedEditable ? handleReopenApproval(review) : null}
            onReassign={(e, newApprover) => handleReassign(review, newApprover)}
            onReject={() => handleOpenApproveModal(review, 'reject')}
            message={review.comment}
            name={review.step_name}
            reviewer={reviewer}
            status={status}
          />
          {isEditActionsVisible && (
            <StyledBudgetRequestApprovalFlowReviewActions>
              <Space size={4}>
                {canDelete && (
                  <Button
                    icon={renderIcon(<TrashIcon />)}
                    onClick={() => handleOpenDeleteModal(review)}
                    size='small'
                    type='text'
                  />
                )}
                <Button
                  icon={renderIcon(<EditLineIcon />)}
                  onClick={() => handleEditApprovalStepModal(review)}
                  size='small'
                  type='text'
                />
              </Space>
            </StyledBudgetRequestApprovalFlowReviewActions>
          )}
        </StyledBudgetRequestApprovalFlowReview>

        {!isLastReview && (
          <StyledBudgetRequestApprovalFlowDivider>
            <Divider type='vertical' style={{ height: isEditActionsVisible ? 40 : 24, margin: '0 auto' }} />
            {isEditActionsVisible && (
              <Button
                icon={<PlusOutlined />}
                size='small'
                onClick={() => handleAddNewStep(review?.step + 1)}
              />
            )}
          </StyledBudgetRequestApprovalFlowDivider>
        )}
      </StyledBudgetRequestApprovalFlowReviewContainer>
    );
  }

  const renderSendForApprovalButton = () => {
    const canEdit = isBudgetOwner || isAdmin;
    const status = canEdit ? 'pending' : 'default';
    const alertMessage = t(`budgets:alerts.${isPOProcurementType ? 'definePOBudgetBeforeSubmit' : 'defineBudgetItemBeforeSubmit'}`);
    return (
      <Space size='middle' direction='vertical' className='w-100'>
        <UserReview
          approveButtonProps={{text: t('sendForApproval'), loading: submitForReviewButtonProps.loading, disabled: submitForReviewButtonProps.disabled || !isEnabledSendForApprovalButton}}
          canEdit={canEdit}
          canReassign={false}
          name={t(`budgets:${isPOProcurementType ? 'requestPO' : 'requestBudgetItem' }`)}
          onApprove={handleSubmitForReview}
          rejectButtonProps={{show: false}}
          reviewer={{...budget.reported_by, label: isBudgetOwner ? t('you') : budget?.full_name}}
          status={status}
        />
        <UnmountClosed isOpened={!isEnabledSendForApprovalButton}>
          <Alert type='warning' message={alertMessage} showIcon />
        </UnmountClosed>
      </Space>
    );
  }

  const handleCancelDeleteModal = () => setDeleteModalProps(DEFAULT_MODAL_PROPS);

  const handleOkDeleteModal = () => {
    if (!budgetId) return;

    setDeleteModalProps({...deleteModalProps, loading: true});

    dispatch(budgetActions.deletePurchaseOrderStep({
      id: budgetId,
      approver: deleteModalProps?.step?.id,
      successCallback: () => {
        handleCancelDeleteModal();
      },
      errorCallback: () => {
        setDeleteModalProps({...DEFAULT_MODAL_PROPS, loading: false});
        alertActions.error(t('budgets:alerts.deleteApprovalStepError'));
      }
    }));
  }

  const handleReopenApproval = (approval) => {
    const approvalId = approval?.id;
    if (!budgetId || !approvalId) return;
    const action = isPOProcurementType ? budgetActions.reopenPurchaseOrderApproval : budgetActions.reopenBudgetRequestApproval

    dispatch(action({
      id: budgetId,
      approvalId,
      errorCallback: () => {
        alertActions.error(t('budgets:alerts.reopenBudgetRequestApprovalError'));
      }
    }));
  }

  const handleOpenDeleteModal = (step) => {
    setDeleteModalProps({...deleteModalProps, open: true, step});
  }

  const handleCloseApprovalStepModal = () => {
    setApprovalStepModalProps({...approvalStepModalProps, open: false, loading: false});
  }

  const handleEditApprovalStepModal = (step) => {
    setApprovalStepModalProps({...approvalStepModalProps, mode: 'edit', open: true, step});
  }

  const handleAddNewStep = (nextStep = 1) => {
    setApprovalStepModalProps({...DEFAULT_MODAL_PROPS, open: true, nextStep, mode: 'add'});
  }

  const handleOkApprovalStepModal = ({data, successCallback, errorCallback}) => {
    if (!budgetId) return;

    const isEditMode = approvalStepModalProps?.mode === 'edit';

    if (isEditMode && !approvalStepModalProps.step) return;

    setApprovalStepModalProps({...approvalStepModalProps, loading: true});

    const action = isEditMode
      ? budgetActions.updatePurchaseOrderStep
      : budgetActions.createPurchaseOrderStep;

    dispatch(action({
      id: budgetId,
      approver: isEditMode ? approvalStepModalProps.step?.id : undefined,
      data: isEditMode ? data : { ...data, step: approvalStepModalProps?.nextStep || approvals.length + 1, step_is_main: false },
      successCallback: (response) => {
        handleCloseApprovalStepModal();
        successCallback?.(response);
      },
      errorCallback: (response) => {
        setApprovalStepModalProps({...approvalStepModalProps, loading: false});
        errorCallback?.(response);
      }
    }));
  }

  const handleOpenApproveModal = (step, mode) => {
    setApprovalModalProps({...approvalModalProps, open: true, step, mode});
  }

  const handleCloseApprovalModal = () => setApprovalModalProps(DEFAULT_MODAL_PROPS);

  const handleOkApprovalModal = ({data, successCallback, errorCallback}) => {
    if (!budgetId || !approvalModalProps?.step) return;

    setApprovalModalProps({...approvalModalProps, loading: true});

    const action = approvalModalProps?.mode === 'approve'
      ? isPOProcurementType ? budgetActions.approvePurchaseOrder : budgetActions.approveBudgetRequest
      : isPOProcurementType ? budgetActions.rejectPurchaseOrder : budgetActions.rejectBudgetRequest;

    dispatch(action({
      id: budgetId,
      approvalId: approvalModalProps.step.id,
      data,
      successCallback: (response) => {
        handleCloseApprovalModal();
        successCallback?.(response);
      },
      errorCallback: (response) => {
        setApprovalModalProps({...approvalModalProps, loading: false});
        errorCallback?.(response);
      }
    }));
  }

  const handleReassign = (step, newApprover) => {
    if (!budgetId || !newApprover) return;

    const action = isPOProcurementType
      ? budgetActions.reassignPurchaseOrder
      : budgetActions.reassignBudgetRequest;

    dispatch(action({
      id: budgetId,
      data: { new_approver: newApprover, approval_id: step?.id },
      errorCallback: (response) => {
        alertActions.error(response?.response?.data?.message || t('budgets:alerts.reassignApprovalStepError'));
      }
    }));
  }

  const handleSubmitForReview = () => {
    if (!budgetId || !isEnabledSendForApprovalButton) return;

    setSubmitForReviewButtonProps({...submitForReviewButtonProps, loading: true});

    const action = isPOProcurementType
      ? budgetActions.submitForReviewPurchaseOrder
      : budgetActions.submitForReviewBudgetRequest;

    dispatch(action({
      id: budgetId,
      successCallback: () => {
        setSubmitForReviewButtonProps({...submitForReviewButtonProps, loading: false, disabled: true});
        alertActions.success(t(`budgets:alerts.${isPOProcurementType ? 'submitPOForReviewSuccess' : 'submitBudgetForReviewSuccess'}`), 5000, 400);
      },
      errorCallback: (response) => {
        setSubmitForReviewButtonProps({...submitForReviewButtonProps, loading: false});
        alertActions.error(response?.response?.data?.message || t('budgets:alerts.submitForReviewError'));
      }
    }));
  }

  return (
    <StyledBudgetRequestApprovalFlow size='large' direction='vertical' {...rest}>
      {isDraftStatus && renderSendForApprovalButton()}
      {!isDraftStatus && (
        <Space className='w-100' direction='vertical' size={10}>
          {approvals.map(renderReview)}
        </Space>
      )}

      <DeleteModal
        onCancel={handleCancelDeleteModal}
        onOk={handleOkDeleteModal}
        okText={getDeleteModalText('okText')}
        title={getDeleteModalText('title')}
        width={480}
        {...deleteModalProps}
      >
        <StyledBudgetRequestApprovalFlowDeleteModal>
          <h5>{getDeleteModalText('content.title')}</h5>
          <p>{getDeleteModalText('content.description')}</p>
        </StyledBudgetRequestApprovalFlowDeleteModal>
      </DeleteModal>

      <AddApprovalStepModal
        {...approvalStepModalProps}
        handleCancel={handleCloseApprovalStepModal}
        handleOk={handleOkApprovalStepModal}
      />

      <ApprovalModal
        {...approvalModalProps}
        handleCancel={handleCloseApprovalModal}
        handleOk={handleOkApprovalModal}
        name={budgetNumber}
        title={approvalModalProps?.step?.step_name}
      />

    </StyledBudgetRequestApprovalFlow>
  );
}

export default BudgetRequestApprovalFlow;
